/**
 * Module dependencies.
 */

import { useCrudAdd } from 'app/hooks/requests/crud/use-crud-add';
import { identity, reduce } from 'lodash';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CrudForm } from './crud-form';
import { CrudAddType, CrudTemplate } from './form-types';
import { resolveFormKeys } from './crud-edit';
import { CrudTemplateProvider } from './crud-template-provider';
import qs from 'qs';

/**
 * `Props` type.
 */

type Props = {
  template: CrudTemplate;
  // When rendered in a drawer there is no add-route query to prefill from, so the
  // caller passes initial values directly and `onDone` closes the drawer instead
  // of navigating away.
  initialValues?: any;
  onDone?: () => void;
};

/**
 * Default normalize inicial values.
 */

function defaultNormalizeInitialValues(formKeys: string[], initialValues: any): any {
  return initialValues;
}

/**
 * Export `CrudAdd` component.
 */

export function CrudAdd({ template, initialValues: initialValuesOverride, onDone }: Props): JSX.Element {
  const {
    formFields,
    redirect,
    normalizePayload = identity,
    submitLabel,
    normalizeInitialValues = defaultNormalizeInitialValues
  } = template.add as CrudAddType;
  const location = useLocation();
  const query = useMemo(() => {
    const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
    return parsed;
  }, [location.search]);

  const routeList = template?.list?.route;
  const params = useParams();
  const computedInitialValues = useMemo(() => {
    const result = reduce(
      template.add?.acceptQueryFields,
      (acc: any, field: any) => {
        const { name, fieldName, parser } =
          typeof field !== 'string'
            ? field
            : {
                name: field,
                fieldName: field,
                parser: identity
              };

        const parsedQueryValue = parser((query as any)?.filters?.[name] ?? (query as any)?.[name]);

        if (parsedQueryValue === undefined || parsedQueryValue === null) {
          return acc;
        }

        return {
          ...acc,
          [fieldName ?? name]: parsedQueryValue
        };
      },
      {}
    );

    return result;
  }, [query, template.add?.acceptQueryFields]);

  const initialValues = useMemo(
    () => ({ ...computedInitialValues, ...(initialValuesOverride ?? {}) }),
    [computedInitialValues, initialValuesOverride]
  );

  const formKeys = useMemo(() => {
    if (!template.add?.formFields) {
      return [];
    }

    return resolveFormKeys(formFields);
  }, [formFields, template.add?.formFields]);

  const { mutate: add } = useCrudAdd(template, params);
  const navigate = useNavigate();
  const values = useMemo(
    () => normalizeInitialValues?.(formKeys, initialValues),
    [formKeys, initialValues, normalizeInitialValues]
  );

  const form = useForm({
    values,
    mode: 'all'
  });

  const onSubmit = async (values: any) => {
    add(await normalizePayload(values), {
      onSuccess: () => {
        if (onDone) {
          onDone();

          return;
        }

        navigate(redirect ?? routeList ?? '');
      }
    });
  };

  return (
    <CrudTemplateProvider template={template}>
      <CrudForm
        form={form}
        formFields={formFields}
        formType={'add'}
        onSubmit={onSubmit as any}
        submitLabel={submitLabel}
      />
    </CrudTemplateProvider>
  );
}
